import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IGetOrderByCustomer } from 'apis/GraphQL/Variables/salesInvoice';
import { SalesInfoApis } from 'apis/SalesInfoApis';
import { SalesInvoiceApis } from 'apis/SalesInvoiceApi';
import { PlusBordered } from 'assets/svg';
import { Button, ButtonFilter, ButtonExport } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { IMergeOrderParams } from 'interfaces/SalesInfo';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import {
  FilterSalesInvoicePage,
  IFilterSaleInvoiceForm,
} from './FilterSalesInvoicePage';
import { MergeOrderModal } from './MergeOrderModal';
import { TableSalesInvoicePage } from './TableSalesInvoicePage';
import { formatCurrency, formatDate } from 'utils/constants/functions';

function SalesInvoicePage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTitle, setBreadcrumb } = useTitle();

  const filterRef = useRef<any>(null);
  const firstRender = useRef(true);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const [isOpenFilter, setIsOpenFilter] = useState(false),
    [edit, setEdit] = useState(-1),
    [editIndex, setEditIndex] = useState(-1),
    [salesInvoiceData, setSalesInvoiceData] = useState(
      {} as {
        items: any;
        pageInfo: any;
      },
    ),
    [filterData, setFilterData] = useState({
      'createFrom - createTo': [
        dayjs().subtract(1, 'month').startOf('day'),
        null,
      ],
    } as Omit<IGetOrderByCustomer, 'page' | 'pageSize'>),
    [isOpenMergeOrderModal, setIsOpenMergeOrderModal] = useState(false),
    [exportLoading, setExportLoading] = useState(false);

  const defaultFilter = useMemo(() => {
    if (user?.isAdmin) return {};

    return {
      ...{ plantId: user?.plantCode ?? undefined },
    };
  }, [user]);

  const {
      mutate: getOrderByCustomerMutate,
      isPending: isGetOrderByCustomerPending,
    } = useMutation({
      mutationFn: (params: IGetOrderByCustomer) =>
        SalesInvoiceApis.getOrderByCustomer(params),
      onSuccess: (res: any) => {
        if (res?.data?.getOrderData) {
          setSalesInvoiceData({
            items: (res?.data?.getOrderData?.items ?? []).map(
              (item: any, index: number) => {
                const parentIndex = index + 1 + pageSize * (page - 1);

                return {
                  ...item,
                  index: parentIndex,
                  SOId:
                    item?.torderProcess?.find(
                      (item: any) => item?.processCode === 'SO',
                    )?.processId ?? '-',
                  mergeOrderIds:
                    item?.tRelationOrder
                      ?.filter((item: any) => item?.statusId === 'OR02_03')
                      .map((item: any) => item?.id)
                      ?.join('; ') || '-',
                  seperateOrderIds:
                    item?.tRelationOrder
                      ?.filter((item: any) => item?.statusId === 'OR02_01')
                      .map((item: any) => item?.id)
                      ?.join('; ') || '-',
                  EInvoiceId:
                    item?.torderProcess?.find(
                      (item: any) => item?.processCode === 'EINVOICE',
                    )?.processId ?? '-',
                  // customer:
                  //   item?.tchildCustomer === null
                  //     ? item?.tcustomer
                  //     : item?.tchildCustomer,
                  children:
                    typeof (item?.tRelationOrder ?? []).filter(
                      (item: any) => item?.statusId === 'OR02_01',
                    ) !== 'undefined'
                      ? item?.tRelationOrder
                          .filter((item: any) => item?.statusId === 'OR02_01')
                          .map((item: any, itemIndex: number) => ({
                            ...item,
                            index: `${parentIndex}.${itemIndex + 1}`,
                            SOId:
                              item?.torderProcess?.find(
                                (item: any) => item?.processCode === 'SO',
                              )?.processId ?? '-',
                            EInvoiceId:
                              item?.torderProcess?.find(
                                (item: any) => item?.processCode === 'EINVOICE',
                              )?.processId ?? '-',
                            customer: item?.tchildCustomer
                              ? item?.tchildCustomer
                              : item?.tcustomer,
                          }))
                      : [],
                };
              },
            ),
            pageInfo: res?.data?.getOrderData?.pageInfo,
          });
          return;
        }

        if (res?.errors) toastError(`Lỗi. ${res?.errors[0]?.message}`);
      },
      onError: (error) => {
        toastError(`Lỗi. ${error?.message}`);
      },
    }),
    { mutate: mergeOrderMutate, isPending: isMergeOrderMutatePending } =
      useMutation({
        mutationFn: (params: IMergeOrderParams) =>
          SalesInfoApis.mergeOrder(params),
        onSuccess: (res: any) => {
          if (res?.data) {
            toastSuccess('Tạo đơn gộp thành công!');

            setIsOpenMergeOrderModal(false);

            if (filterRef && filterRef?.current)
              filterRef?.current?.handleOnCancel();

            return;
          }

          toastError(`Tạo đơn gộp thất bại. ${res?.message}`);
        },
        onError: (error) => {
          toastError(`Gộp đơn thất bại. ${error?.message ?? error}`);
        },
      });

  useEffect(() => {
    setTitle('Bảng kê bán hàng');
    setBreadcrumb([
      { title: 'Kế toán/thủ quỹ' },
      { title: 'Bảng kê bán hàng' },
    ]);
  }, []);

  useEffect(() => {
    if (firstRender.current) return;

    getOrderByCustomerMutate({
      ...filterData,
      ...defaultFilter,
      page,
      pageSize,
    });
  }, [firstRender, filterData, page, pageSize]);

  useEffect(() => {
    if (!firstRender.current) return;
    firstRender.current = false;

    const searchParams = new URLSearchParams(location.search);
    const params = Object.fromEntries(searchParams);

    const { createFrom, createTo } = params;
    const formattedParams: IFilterSaleInvoiceForm = {
      ...params,
      'createFrom - createTo': [
        createFrom
          ? dayjs(createFrom, 'DD/MM/YYYY')
          : dayjs().subtract(1, 'month').startOf('day'),
        createTo && dayjs(createTo, 'DD/MM/YYYY'),
      ],
    };

    if (filterRef.current)
      filterRef.current.handleSetFormValues({
        ...formattedParams,
        ...defaultFilter,
      });

    setIsOpenFilter(Object.keys(params).length > 2);
    setFilterData({
      ...formattedParams,
      teamSalesId: params?.teamSalesId?.split('.')?.[0],
    });
  }, [location, firstRender, defaultFilter]);

  const handleCombineOrders = useCallback(() => {
    setIsOpenMergeOrderModal(true);
  }, []);

  const handleFinishFilter = useCallback(
    (params: any) => {
      const queryParams = Object.entries(params).reduce(
        (preVal, currentVal) => {
          const [key, value] = currentVal;

          if (value) {
            if (key.includes('createFrom - createTo') && Array.isArray(value)) {
              const [createFrom, createTo] = value;

              createFrom &&
                (preVal['createFrom'] = dayjs(createFrom)
                  .format('DD/MM/YYYY')
                  ?.toString());

              createTo &&
                (preVal['createTo'] = dayjs(createTo)
                  .format('DD/MM/YYYY')
                  ?.toString());
            } else preVal[key] = value;
          }

          return preVal;
        },
        { page: 1, page_size: pageSize } as Record<string, any>,
      );

      navigate(
        {
          pathname: location.pathname,
          search: queryString.stringify(queryParams, {
            sort: (a, b) => {
              if (a === 'page') return -1;
              if (b === 'page') return 1;
              if (a === 'page_size') return -1;
              if (b === 'page_size') return 1;

              return 0;
            },
          }),
        },
        { replace: true },
      );

      setFilterData({
        ...params,
        teamSalesId: params?.teamSalesId?.split('.')?.[0],
      });
    },
    [location, pageSize],
  );

  const handleCancelFilter = useCallback(() => {
    setFilterData({
      'createFrom - createTo': [
        dayjs().subtract(1, 'month').startOf('day'),
        null,
      ],
    });

    navigate(`?page=1&page_size=${pageSize}`);
  }, [pageSize]);

  const handleMergeOrder = useCallback((values: any) => {
    if (!values?.orderIds || (values?.orderIds ?? []).length <= 0) return;

    mergeOrderMutate({ orderIds: values.orderIds.join(',') });
  }, []);

  const handleCancelMergeOrderModal = useCallback(() => {
    setIsOpenMergeOrderModal(false);
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (!salesInvoiceData?.pageInfo?.totalItems) {
      toastError('Không có dữ liệu để xuất');
      return;
    }

    setExportLoading(true);
    try {
      const response = await SalesInvoiceApis.getOrderByCustomer({
        ...filterData,
        ...defaultFilter,
        page: 1,
        pageSize: salesInvoiceData.pageInfo.totalItems,
      });

      if (response?.data?.getOrderData?.items) {
        const exportData = response.data.getOrderData.items.map(
          (item: any, index: number) => {
            // Xử lý dữ liệu giống như trong table
            const customerSapId =
              item.tchildCustomer == null
                ? item.tcustomer?.sapCustomerId
                : item.tchildCustomer.sapCustomerId;

            const customerName =
              item.tchildCustomer == null
                ? item.tcustomer?.storeName
                : item.tchildCustomer.storeName;

            return {
              ...item,
              index: index + 1,
              SOId:
                item?.torderProcess?.find(
                  (item: any) => item?.processCode === 'SO',
                )?.processId ?? '-',
              mergeOrderIds:
                item?.tRelationOrder
                  ?.filter((item: any) => item?.statusId === 'OR02_03')
                  .map((item: any) => item?.id)
                  ?.join('; ') || '-',
              seperateOrderIds:
                item?.tRelationOrder
                  ?.filter((item: any) => item?.statusId === 'OR02_01')
                  .map((item: any) => item?.id)
                  ?.join('; ') || '-',
              EInvoiceId:
                item?.torderProcess?.find(
                  (item: any) => item?.processCode === 'EINVOICE',
                )?.processId ?? '-',
              customerSapId,
              customerName,
              // Format các trường tiền tệ
              totalProcessPrice:
                item.totalProcessPrice !== null &&
                item.totalProcessPrice !== undefined
                  ? formatCurrency({
                      value: item.totalProcessPrice,
                      round: true,
                    })
                  : '-',
              totalPrice:
                item.totalPrice !== null && item.totalPrice !== undefined
                  ? formatCurrency({ value: item.totalPrice, round: true })
                  : '-',
              finalPriceSap:
                item.finalPriceSap !== null && item.finalPriceSap !== undefined
                  ? formatCurrency({ value: item.finalPriceSap, round: true })
                  : '-',
              goldWeightOfStool: item.goldWeightOfStool
                ? formatCurrency({ value: item.goldWeightOfStool, round: true })
                : '-',
              // Format ngày tháng
              createDate: item.createDate ? formatDate(item.createDate) : '-',
              modifyDate: item.modifyDate ? formatDate(item.modifyDate) : '-',
            };
          },
        );

        const columns = [
          { title: 'Mã SO', dataIndex: 'SOId', width: 100 },
          { title: 'Mã đơn gộp', dataIndex: 'mergeOrderIds', width: 120 },
          { title: 'Mã bảng kê', dataIndex: 'id', width: 160 },
          { title: 'Mã đơn tách', dataIndex: 'seperateOrderIds', width: 150 },
          { title: 'Khách hàng', dataIndex: 'customerSapId', width: 120 },
          { title: 'Tên khách hàng', dataIndex: 'customerName', width: 250 },
          {
            title: 'Mã KH cha',
            dataIndex: ['tcustomer', 'sapCustomerId'],
            width: 100,
          },
          {
            title: 'Mã KH check in',
            dataIndex: ['tcustomer', 'sapCustomerId'],
            width: 120,
          },
          {
            title: 'Thành tiền công',
            dataIndex: 'totalProcessPrice',
            width: 150,
          },
          { title: 'Thành tiền món', dataIndex: 'totalPrice', width: 150 },
          {
            title: 'Thành tiền sau CK',
            dataIndex: 'finalPriceSap',
            width: 150,
          },
          { title: 'Loại bảng kê', dataIndex: 'orderTypeName', width: 100 },
          { title: 'SL Sản phẩm', dataIndex: 'quantity', width: 100 },
          { title: 'T.L vàng', dataIndex: 'goldWeightOfStool', width: 150 },
          { title: 'HĐĐT', dataIndex: 'EInvoiceId', width: 200 },
          { title: 'Tên kế toán', dataIndex: 'accountantName', width: 150 },
          {
            title: 'Kế toán xác nhận',
            dataIndex: 'accountantConfirm',
            width: 150,
          },
          {
            title: 'Thời gian kế toán xác nhận',
            dataIndex: 'accountantConfirmTime',
            width: 250,
          },
          {
            title: 'Thủ quỹ xác nhận',
            dataIndex: 'treasurerConfirm',
            width: 150,
          },
          {
            title: 'Thời gian thủ quỹ xác nhận',
            dataIndex: 'treasurerConfirmTime',
            width: 250,
          },
          { title: 'Chi nhánh', dataIndex: 'plantId', width: 100 },
          { title: 'Trạng thái', dataIndex: 'orderStatusSyn', width: 100 },
          { title: 'Người tạo', dataIndex: 'createName', width: 100 },
          { title: 'Ngày tạo', dataIndex: 'createDate', width: 200 },
          { title: 'Người cập nhật', dataIndex: 'modifyName', width: 100 },
          { title: 'Ngày cập nhật', dataIndex: 'modifyDate', width: 200 },
        ];

        exportExcel_v2(columns, exportData, 'Bảng kê bán hàng');
        toastSuccess('Xuất Excel thành công!');
      }
    } catch (error) {
      toastError('Xuất Excel thất bại!');
    } finally {
      setExportLoading(false);
    }
  }, [salesInvoiceData, filterData, defaultFilter]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
            disabled={edit !== -1}
          />
        </Col>
        <Col>
          <ButtonExport
            loading={exportLoading}
            onClick={handleExportExcel}
            disabled={!salesInvoiceData?.pageInfo?.totalItems}
          />
        </Col>
        {user?.isAdmin || user?.isTreasurer ? (
          <></>
        ) : (
          <Col>
            <Button
              fill
              iconBefore={<PlusBordered className="white" />}
              customClass="!bg-[#038F00] !border-[#038F00]"
              title="Gộp đơn"
              handleOnclick={handleCombineOrders}
              disabled={edit !== -1}
            />
          </Col>
        )}
        {/* <Col>
          <Button
            fill
            iconBefore={<UserAddOuntLined />}
            title="Bàn giao"
            handleOnclick={handleCombineOrders}
            disabled={edit !== -1}
          />
        </Col> */}
      </Row>
      <FilterSalesInvoicePage
        ref={filterRef}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleFinishFilter}
        onCancel={handleCancelFilter}
      />
      <TableSalesInvoicePage
        data={salesInvoiceData}
        editIndex={editIndex}
        loading={isGetOrderByCustomerPending}
      />
      <MergeOrderModal
        open={isOpenMergeOrderModal}
        loading={isMergeOrderMutatePending}
        onOk={handleMergeOrder}
        onCancel={handleCancelMergeOrderModal}
      />
    </>
  );
}

export default SalesInvoicePage;
